/**
 * Pure card logic — no React / UI dependencies.
 * Shared between ManagerCardHand (rendering) and tests.
 */

import { QUALIFYING_GP_FIELDER, QUALIFYING_IP_STARTER, QUALIFYING_SV_RELIEVER } from "@/constants/game";
export { QUALIFYING_GP_FIELDER, QUALIFYING_IP_STARTER, QUALIFYING_SV_RELIEVER };

export function careerBestQualifyingFielder(player, field) {
  const seasons = (player?.seasons) || [];
  let best = null;
  for (const s of seasons) {
    const stats = s?.stats || {};
    if ((stats.games_played ?? 0) < QUALIFYING_GP_FIELDER) continue;
    const v = stats[field];
    if (v == null) continue;
    if (best == null || v > best) best = v;
  }
  return best;
}

export function careerBestQualifyingPitcher(player, field) {
  const seasons = (player?.seasons) || [];
  let best = null;
  for (const s of seasons) {
    const stats = s?.stats || {};
    const isStarter = (stats.innings_pitched ?? 0) >= QUALIFYING_IP_STARTER;
    const isReliever = (stats.saves ?? 0) >= QUALIFYING_SV_RELIEVER;
    if (!isStarter && !isReliever) continue;
    const v = stats[field];
    if (v == null) continue;
    if (best == null || v > best) best = v;
  }
  return best;
}

export function careerBestAtMostPitcher(player, field) {
  const seasons = (player?.seasons) || [];
  let best = null;
  for (const s of seasons) {
    const stats = s?.stats || {};
    const isStarter = (stats.innings_pitched ?? 0) >= QUALIFYING_IP_STARTER;
    const isReliever = (stats.saves ?? 0) >= QUALIFYING_SV_RELIEVER;
    if (!isStarter && !isReliever) continue;
    const v = stats[field];
    if (v == null) continue;
    if (best == null || v < best) best = v;
  }
  return best;
}

export function evalCondition(cond, ctx) {
  if (!cond || typeof cond !== "object") return true;
  const { gameState, currentBatter, currentPitcher } = ctx;
  switch (cond.kind) {
    case "runner_3b_career_sb": {
      const runner = gameState?.bases?.third;
      if (!runner) return false;
      const best = careerBestQualifyingFielder(runner, "stolen_bases");
      return best != null && best >= (cond.threshold || 50);
    }
    case "runner_any_career_sb": {
      const bases = gameState?.bases || {};
      const runners = [bases.first, bases.second, bases.third].filter(Boolean);
      const threshold = cond.threshold || 40;
      return runners.some(r => {
        const best = careerBestQualifyingFielder(r, "stolen_bases");
        return best != null && best >= threshold;
      });
    }
    case "batter_career_avg": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "batting_average");
      return best != null && best >= (cond.threshold || 0.300);
    }
    case "batter_career_doubles": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "doubles");
      return best != null && best >= (cond.threshold || 40);
    }
    case "batter_career_hr": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "home_runs");
      return best != null && best >= (cond.threshold || 45);
    }
    case "batter_career_hits": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "hits");
      return best != null && best >= (cond.threshold || 185);
    }
    case "batter_career_rbi_with_runners_in_scoring": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "rbi");
      if (best == null || best < (cond.threshold || 100)) return false;
      const bases = gameState?.bases || {};
      return !!(bases.second || bases.third);
    }
    case "steal_runner_career_sb": {
      const runner = gameState?._activeStealRunner || null;
      const stealBase = gameState?._activeStealBase || null;
      if (!runner) return false;
      if (cond.exclude_home && stealBase === "third") return false;
      const best = careerBestQualifyingFielder(runner, "stolen_bases");
      return best != null && best >= (cond.threshold || 30);
    }
    case "pitcher_career_ip": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "innings_pitched");
      return best != null && best >= (cond.threshold || 200);
    }
    case "pitcher_career_so": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "strikeouts");
      return best != null && best >= (cond.threshold || 180);
    }
    case "pitcher_career_saves": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "saves");
      return best != null && best >= (cond.threshold || 30);
    }
    case "pitcher_career_bb": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "walks");
      return best != null && best >= (cond.threshold || 60);
    }
    case "pitcher_career_era_under": {
      if (!currentPitcher) return false;
      const best = careerBestAtMostPitcher(currentPitcher, "era");
      return best != null && best <= (cond.threshold || 2.50);
    }
    case "state_two_outs":
      return (gameState?.outs ?? 0) >= 2;
    case "state_called_ball":
      return true;
    case "state_pitcher_batters_faced_gt": {
      const threshold = cond.threshold ?? 6;
      return (gameState?.batters_faced_inning ?? 0) > threshold;
    }
    case "state_last_inning": {
      const total = cond.total_innings || gameState?.total_innings || 9;
      return (gameState?.inning ?? 1) >= total;
    }
    case "state_opp_pitcher_pitched_over_half": {
      // Worn Down: opposing (currently-pitching) pitcher must have worked
      // more than half the game's innings. Matches server logic at
      // backend/server.py evalCondition's `state_opp_pitcher_pitched_over_half`.
      const inning = gameState?.inning ?? 1;
      const total = gameState?.total_innings ?? cond.total_innings ?? 9;
      const half = gameState?.half || "top";
      const pitchingRole = half === "top" ? "home" : "away";
      // If the entered-inning field isn't on state (e.g. starter never replaced),
      // default to 1 — matches the server's `or 1` fallback.
      const entered = gameState?.[`${pitchingRole}_pitcher_entered_inning`] ?? 1;
      return (inning - entered) > (total / 2);
    }
    case "all_of": {
      const subs = cond.conditions || [];
      return subs.every(sub => evalCondition(sub, ctx));
    }
    case "any_of": {
      const subs = cond.conditions || [];
      return subs.some(sub => evalCondition(sub, ctx));
    }
    case "state_leadoff_batter": {
      const outs = gameState?.outs ?? 0;
      const bases = gameState?.bases || {};
      return outs === 0 && !bases.first && !bases.second && !bases.third;
    }
    case "batter_combined_season": {
      if (!currentBatter) return false;
      const pairs = cond.pairs || [];
      const seasons = (currentBatter?.seasons) || [];
      return seasons.some(s => {
        const stats = s?.stats || {};
        if ((stats.games_played ?? 0) < QUALIFYING_GP_FIELDER) return false;
        return pairs.every(p => {
          const v = stats[p.stat];
          if (v == null) return false;
          return p.cmp === "lte" ? v <= p.threshold : v >= p.threshold;
        });
      });
    }
    case "batter_career_avg_at_most": {
      if (!currentBatter) return false;
      const seasons = (currentBatter?.seasons) || [];
      const threshold = cond.threshold ?? 0.240;
      return seasons.some(s => {
        const stats = s?.stats || {};
        if ((stats.games_played ?? 0) < QUALIFYING_GP_FIELDER) return false;
        const v = stats.batting_average;
        return v != null && v <= threshold;
      });
    }
    case "state_walk_occurred":
      return !!(gameState?.pending_bogo_walk);
    case "state_hbp_occurred":
      return !!(gameState?.last_result_was_hbp);
    case "state_hr_incoming":
      return !!(gameState?.last_result_was_hr_incoming);
    case "state_hr_multi_run":
      return !!(gameState?.last_result_was_hr_multi_run);
    case "runner_lead_career_sb": {
      const bases = gameState?.bases || {};
      const leadRunner = bases.third || bases.second || bases.first;
      if (!leadRunner) return false;
      if (bases.third) return false;
      const best = careerBestQualifyingFielder(leadRunner, "stolen_bases");
      return best != null && best >= (cond.threshold || 35);
    }
    case "state_consecutive_300_batters": {
      const threshold = cond.threshold || 0.280;
      if (!currentBatter) return false;
      const curBest = careerBestQualifyingFielder(currentBatter, "batting_average");
      if (curBest == null || curBest < threshold) return false;
      const onDeck = gameState?._onDeckBatter;
      if (!onDeck) return true;
      const deckBest = careerBestQualifyingFielder(onDeck, "batting_average");
      return deckBest != null && deckBest >= threshold;
    }
    case "batter_career_rbi": {
      const best = careerBestQualifyingFielder(currentBatter, "rbi");
      return best != null && best >= (cond.threshold || 100);
    }
    case "state_wild_pitch":
      return !!(gameState?.last_result_was_wild_pitch);
    case "state_wild_pitch_men_on": {
      if (!gameState?.last_result_was_wild_pitch) return false;
      const bases = gameState?.bases || {};
      return !!(bases.first || bases.second || bases.third);
    }
    case "state_batters_faced_gte":
      return (gameState?.batters_faced_inning || 0) >= (cond.threshold || 1);
    case "state_batter_called_ball_wrong":
      return !!(gameState?.last_result_was_called_ball_wrong);
    case "state_correct_called_ball":
      return !!(gameState?.last_result_was_correct_called_ball);
    case "state_correct_chip_call":
      return true;
    case "state_opponent_has_active_card":
      return (gameState?._opponentActiveCards ?? 0) > 0;
    case "state_opponent_just_played_card":
      return true;
    case "state_runner_on_base": {
      const bases = gameState?.bases || {};
      return !!(bases.first || bases.second || bases.third);
    }
    case "state_discard_not_empty":
      return (gameState?._discard_count ?? 0) > 0;
    case "state_clean_count":
      return (gameState?.balls ?? 0) === 0 && (gameState?.strikes ?? 0) === 0;
    case "state_reliever_in_bullpen":
      return !!(gameState?._reliever_available);
    case "state_last_roll_exists":
      return !!(gameState?._lastRollExists);
    case "fielding_team_has_rookie":
      return !!(gameState?._fieldingTeamHasRookie);
    case "batter_last_season_peak_categories": {
      if (!currentBatter) return false;
      const seasons = currentBatter.seasons || [];
      if (seasons.length < 2) return false;
      const cats = cond.categories || ["hits", "doubles", "rbi"];
      const last = seasons[seasons.length - 1]?.stats || {};
      const prior = seasons.slice(0, -1);
      return cats.some(cat => {
        const lastVal = last[cat] ?? 0;
        const priorBest = Math.max(...prior.map(s => s?.stats?.[cat] ?? 0));
        return lastVal >= priorBest;
      });
    }
    case "pitcher_last_season_peak_categories": {
      if (!currentPitcher) return false;
      const seasons = currentPitcher.seasons || [];
      if (seasons.length < 2) return false;
      const cats = cond.categories || ["wins", "strikeouts", "era"];
      const lowerBetter = new Set(cond.lower_is_better_categories || ["era"]);
      const last = seasons[seasons.length - 1]?.stats || {};
      const prior = seasons.slice(0, -1);
      return cats.some(cat => {
        const lastVal = last[cat] ?? 0;
        if (lowerBetter.has(cat)) {
          const validPrior = prior.map(s => s?.stats?.[cat] ?? 0).filter(v => v > 0);
          return lastVal > 0 && validPrior.length > 0 && lastVal <= Math.min(...validPrior);
        }
        const priorBest = Math.max(...prior.map(s => s?.stats?.[cat] ?? 0));
        return lastVal >= priorBest;
      });
    }
    case "state_score_not_leading": {
      const half = gameState?.half || "top";
      const myScore = half === "top"
        ? (gameState?.away_score || 0)
        : (gameState?.home_score || 0);
      const oppScore = half === "top"
        ? (gameState?.home_score || 0)
        : (gameState?.away_score || 0);
      return myScore <= oppScore;
    }
    case "state_score_deficit_gte": {
      const half = gameState?.half || "top";
      const myScore = half === "top" ? (gameState?.away_score || 0) : (gameState?.home_score || 0);
      const oppScore = half === "top" ? (gameState?.home_score || 0) : (gameState?.away_score || 0);
      return (oppScore - myScore) >= (cond.threshold ?? 3);
    }
    case "pitcher_season_wins_at_most": {
      if (!currentPitcher) return false;
      const threshold = cond.threshold ?? 5;
      return (currentPitcher.seasons || []).some(s => {
        const stats = s?.stats || {};
        if ((stats.innings_pitched ?? 0) < QUALIFYING_IP_STARTER) return false;
        const v = stats.wins;
        return v != null && v <= threshold;
      });
    }
    case "batter_career_rbi_under": {
      if (!currentBatter) return false;
      const best = careerBestQualifyingFielder(currentBatter, "rbi");
      return best == null || best < (cond.threshold ?? 40);
    }
    case "pitcher_career_losses": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "losses");
      return best != null && best >= (cond.threshold ?? 15);
    }
    case "pitcher_career_wins": {
      if (!currentPitcher) return false;
      const best = careerBestQualifyingPitcher(currentPitcher, "wins");
      return best != null && best >= (cond.threshold ?? 16);
    }
    default:
      return true;
  }
}

export function phaseAllowed(cardPhase, currentPhase) {
  if (!cardPhase) return true;
  if (Array.isArray(cardPhase)) return cardPhase.includes("any") || cardPhase.includes(currentPhase);
  return cardPhase === "any" || cardPhase === currentPhase;
}

export function canPlayCard(card, { phase = "any", gameState = null, currentBatter = null, currentPitcher = null, teamRole = null } = {}) {
  if (!card) return false;
  if (!phaseAllowed(card.phase, phase)) return false;
  if (teamRole && card.who && card.who !== "either") {
    const needsPitcher = card.who === "pitcher" || card.who === "pitching" || card.who === "fielding";
    const needsBatter = card.who === "batter" || card.who === "batting";
    if (needsPitcher && teamRole !== "pitcher") return false;
    if (needsBatter && teamRole !== "batter") return false;
  }
  if (card.effect_type === "free_base" && gameState?.base_running_card_used) return false;
  if (card.effect_type === "iconic_tough_hop") {
    const c = gameState?._fielding_contact ?? 0;
    if (c < 1 || c > 4) return false;
    if (!gameState?._toughHopHasEligible) return false;
  }
  if (card.effect_type === "potential_gapper") {
    if ((gameState?._fielding_contact ?? 0) !== 6) return false;
    const hasSB = (currentBatter?.seasons || []).some(
      s => (s?.stats?.stolen_bases ?? 0) >= 30
    );
    if (!hasSB) return false;
  }
  if (card.effect_type === "triple_play") {
    const c = gameState?._fielding_contact ?? 0;
    if (c < 1 || c > 2) return false;
    if ((gameState?.outs ?? 1) !== 0) return false;
    const bases = gameState?.bases ?? {};
    if (!bases.first || !bases.second) return false;
  }
  if (card.effect_type === "iconic_through_the_legs") {
    const c = gameState?._fielding_contact ?? 0;
    if (c < 1 || c > 2) return false;
  }
  if (card.effect_type === "pickoff") {
    // Pickoff is only valid between pitches — disable while a ball is in play.
    if (gameState?._fielding_contact != null) return false;
  }
  const cond = card.condition;
  if (typeof cond === "string") {
    if (cond === "runner_on_base") {
      const bases = gameState?.bases || {};
      if (!bases.first && !bases.second && !bases.third) return false;
    }
    if (cond === "runner_on_second") {
      // Tipping Pitches activation window — gate UI before submission so the
      // server's rejection doesn't surprise the player.
      if (!(gameState?.bases?.second)) return false;
    }
  } else if (cond && typeof cond === "object") {
    if (!evalCondition(cond, { gameState, currentBatter, currentPitcher })) return false;
  }
  return true;
}
